import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import {
  dismissPushPrompt,
  isPushPromptDismissed,
  pushSupported,
  subscribeToPush,
  fetchVapidPublicKey,
} from "../lib/pushNotifications";
import { toast } from "sonner";

/**
 * Soft prompt after login — enable Web Push for yard alerts when the app is backgrounded.
 */
export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user || !pushSupported() || isPushPromptDismissed()) {
      setShow(false);
      return undefined;
    }
    if (Notification.permission === "granted" || Notification.permission === "denied") {
      setShow(false);
      return undefined;
    }
    fetchVapidPublicKey()
      .then((key) => {
        if (!cancelled && key) setShow(true);
      })
      .catch(() => {
        if (!cancelled) setShow(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!show) return null;

  const enable = async () => {
    try {
      setBusy(true);
      const result = await subscribeToPush();
      if (result.ok) {
        toast.success("Notifications enabled");
        setShow(false);
      } else if (result.reason === "denied") {
        toast.error("Notification permission blocked in browser settings");
        dismissPushPrompt();
        setShow(false);
      } else if (result.reason === "not_configured") {
        toast.error("Push is not configured on the server yet");
        setShow(false);
      } else {
        toast.error("Could not enable notifications on this device");
      }
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    dismissPushPrompt();
    setShow(false);
  };

  return (
    <div
      className="fixed left-4 right-4 z-50 mx-auto max-w-md rounded-2xl border border-bansal-sage/30 bg-card p-4 shadow-xl bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-4 sm:left-auto sm:right-4"
      role="region"
      aria-label="Enable notifications"
      data-testid="push-notification-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-bansal-sage/15 text-primary">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">Enable yard alerts</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Get notified for mid-weigh requests, exit approvals, lab updates, and unloading assignments — even when the app is closed.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              size="sm"
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={enable}
              disabled={busy}
            >
              {busy ? "Enabling…" : "Enable"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={dismiss} disabled={busy}>
              Not now
            </Button>
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
          onClick={dismiss}
          aria-label="Dismiss notification prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
